export enum Stages {
  STAGE_II_FINISHED = 'stage_II_finished',
  FINISHED = 'finished',
  STAGE_I = 'stage_I',
  STAGE_I_DOCUMENTS = 'stage_I_documents',
  STAGE_I_FINISHED = 'stage_I_finished',
  STAGE_II = 'stage_II',
  CONTACT_US = 'contact_us',
  NOT_QUALIFY = 'not_qualified',
  STAGE_III = 'stage_III',
  STAGE_III_FINISHED = 'stage_III_finished',
  STAGE_III_CONFLICT = 'stage_III_conflict',
  STAGE_IV_COMPLEMENTARY = 'stage_IV_complementary',
  STAGE_IV = 'stage_IV',
  STAGE_IV_CONFLICT = 'stage_IV_conflict',
  STAGE_IV_FINISHED = 'stage_IV_finished',
  STAGE_V = 'stage_V',
  STAGE_V_FINISHED = 'stage_V_finished',
  STAGE_VI = 'stage_VI',
  OFFER = 'offer',
  OFFER_FINISHED = 'offer_finished',
  // Current (unpatched) e-commerce backend contract:
  PAYMENT = 'payment',
  PAYMENT_COMPLETED = 'payment_completed',
  // Target e-commerce backend contract (not live yet) — keep both sets
  // recognized so the FE doesn't break against whichever is deployed.
  STAGE_ORDER = 'stage_order',
  PAYMENT_SUCCESS = 'stage_payment_success',
  PAYMENT_FAILED = 'stage_payment_failed',
}

export const offerStages = [Stages.STAGE_III, Stages.STAGE_III_CONFLICT];
export const offerStagesHH = [Stages.STAGE_IV, Stages.STAGE_IV_CONFLICT];

export const TYPE_OF_DAMAGE = {
  PART_OC: 'part_oc',
  FULL_OC: 'full_oc',
  PART_AC: 'part_ac',
  FULL_AC: 'full_ac',
} as const;

type TypeOfDamageValues = typeof TYPE_OF_DAMAGE;
type TypeOfDamageKeys = keyof TypeOfDamageValues;

export type TypeOfDamage = TypeOfDamageValues[TypeOfDamageKeys];

export const summaryStages = [
  Stages.STAGE_IV,
  Stages.STAGE_IV_FINISHED,
  Stages.STAGE_V,
  Stages.STAGE_V_FINISHED,
  Stages.STAGE_VI, // -> FINISHED
];

export enum Steps {
  START = 'start',
  DAMAGE_VERIFICATION = 'damage_verification',
  DOCUMENTS = 'documents',
  ADDITIONAL_INFORMATION = 'additional_information',
  COMPLETING_DOCUMENTS = 'completing_documents',
  OFFER = 'offer',
  COMPLEMENTARY_FORM = 'complementary_form',
  TRANSFER_DETAILS = 'transfer_details',
  OWNER_DETAILS = 'owner_details',
  FINISHED = 'finished',
  // HouseHold steps
  None = 'none',
  Step_I_start = 'step_I_start',
  Step_I_finish = 'step_I_finish',
  Step_II_start = 'step_II_start',
  Step_II_finish = 'step_II_finish',
  Step_III_start = 'step_III_start',
  Step_III_finish = 'step_III_finish',
  Offer_start = 'offer_start',
  Offer_rejected = 'offer_rejected',
  Offer_finished = 'offer_finished',
}

export enum StagesHH {
  FINISHED = 'finished',
  STAGE_I = 'stage_I',
  STAGE_II = 'stage_II',
  STAGE_III = 'stage_III',
  CONTACT_US = 'contact_us',
  NOT_QUALIFY = 'not_qualified',
}

export enum DocumentStatuses {
  NOT_SENT = 'not_sent',
  SENT = 'sent',
  DELIVERED = 'delivered',
  COMPLETED = 'completed',
}

interface ConsultantCompleted {
  stage_I: string | null;
  stage_I_documents: string | null;
  stage_II: string | null;
  stage_III: string | null;
}

export enum InjuryType {
  FULL_OC = 'full_oc',
  PART_OC = 'part_oc',
  FULL_AC = 'full_ac',
  PART_AC = 'part_ac',
}

export const injuryTypeMapper = {
  [InjuryType.PART_OC]: 'Szkoda częściowa',
  [InjuryType.FULL_OC]: 'Szkoda całkowita',
  [InjuryType.PART_AC]: 'Szkoda częściowa',
  [InjuryType.FULL_AC]: 'Szkoda całkowita',
};

export interface InsurancePolicy {
  id: number;
  insurance_policy: string;
  insurance_logo: string;
  damage_number: string;
  damage_date: string;
  insurance_type: string;
  injury_type: 0 | 1;
  value_according_to_insurer: number;
}

export interface AdditionalInfo {
  car: string;
  car_year: string;
  car_model: string;
  car_logo?: string;
  car_plate?: string;
  issue_type?: EnumLike;
  vin?: string;
  wheel_side?: string;
  accident_in_poland?: string;
  insurance_policy?: InsurancePolicy;
  postal_code?: string;
}

export interface Product {
  id: number;
  additional_info: AdditionalInfo;
}
export enum ConflictTypes {
  WITHOUT_PESEL = 'without_pesel',
  VALIDATION = 'validation',
  TOO_MANY_COOWNERS = 'too_many_coowners',
  CONTRACTS_ERROR = 'contracts_error',
  CONFLICTS_STAGE_IV = 'conflicts_stage_iv',
  CONFLICTS_STAGE_IV_COMPLEMENTARY = 'conflicts_stage_iv_complementary',
}

export interface Offer {
  opportunity_id: number;
  created_at?: string;
  expire_date?: string;
  surcharge?: number;
  offer_rejected?: boolean;
  surcharge_summary?: number;
  tax?: number;
  re_offer?: boolean;
  opened_at?: string;
  decision_at?: string;
  income_tax?: number;
  business_type?: BUSINESS_TYPE;
  purchase_id?: number;
  type_of_damage: TypeOfDamage;
  is_complementary?: boolean;
  with_old_offer?: boolean;
  hash?: string;
}

export interface User {
  name: string;
  phone: string;
  email: string;
  first_name: string;
  last_name: string | null;
  postal_code: string;
}

export interface GeneratedDocumentsStatuses {
  full_name: string;
  status: DocumentStatuses;
}

interface Attachments {
  name: string;
  size: number;
  url: string;
}

export type FormData = {
  id: number;
  is_blocked_for_client: boolean;
  is_email_empty: boolean;
  product: Product;
  product_id?: number;
  site?: string;
  // Case card only (GET /api/case-card/:hash). The plot flags are present only for
  // leads of a product with the transmission easement service.
  first_name?: string | null;
  is_plot_address_empty?: boolean;
  is_title_register_number_empty?: boolean;
  stage: Stages;
  step: Steps | null;
  offer: Offer;
  user: User;
  crm_user?: {
    name: string;
    avatar: string;
  };
  payload: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<any, any>;
    stepper: {
      step: number;
      innerStep: { [key: number]: number };
    };
  } | null;
  conflict?: ConflictTypes;
  conflicts?: ConflictTypes[];
  consultant_completed_at: ConsultantCompleted | null;
  documents: GeneratedDocumentsStatuses[];
  send_for_signature: string;
  attachments?: Attachments[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  complementary_default_values: Record<any, any>;
  // Only present for the e-commerce (transmission) hash flow, once stage === payment
  payment_url?: string | null;
};

export type Ratings = {
  google: {
    count: number;
    rate: number;
  };
  facebook: {
    count: number;
    rate: number;
  };
  trustpilot: {
    count: number;
    rate: number;
  };
};

export type EnumLike = {
  value: number;
  label: string;
};

export type PaymentMethod = {
  id: number;
  name: string;
  imgUrl: string;
};

interface ResponseData {
  hash: string;
  pixlid: number;
  case_card: string;
  self_service: string;
  // Only present when the lead is eligible for the e-commerce checkout flow
  // (e.g. transmission line easement) — a properly formatted self-service
  // link, unlike `hash` above which is not `{lead_id}`-shaped.
  e_commerce?: string;
}

export interface ApiResponse {
  status: number;
  message: string;
  data: ResponseData;
}

export const BUSINESS_TYPE = {
  JDG: 'jdg',
  ZOO: 'zoo',
  SC: 'sc',
  SJ: 'sj',
  SA: 'sa',
  SK: 'sk',
  SKA: 'ska',
} as const;

export type BUSINESS_TYPE = (typeof BUSINESS_TYPE)[keyof typeof BUSINESS_TYPE];

type OfferNew = {
  hash: string;
  title: string;
  forecastValuePLN: number;
  netPurchasePLN: number | null;
  helpfindFeePercent: number | null;
  payoutTerm: string;
  accepted: boolean;
};

type Cost = {
  hash: string;
  title: string;
  initialFeePLN: number | null;
  courtFeePLN: number | null;
  proceduralRepresentationPLN: number | null;
  expertOpinionPLN: string | null;
  attorneyCostPLN: number | null;
  riskBearer: string;
};

type Accident = {
  insurerCompany: string | null;
  nrDamageFile: string;
  date: string;
};

type Car = {
  make: string;
  model: string;
  year: string;
  plate: string;
};

export type OfferData = {
  accident: Accident;
  car: Car;
  offers: OfferNew[];
  costs: Cost[];
};
